import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { DefaultIntlFormatValues, EqualWidthMessageColumns } from '@ratehub/base-ui';

import MESSAGES from '../definitions/Messages';
import CreditCardShape from '../definitions/CreditCardShape';
import FooterDefaultIntlFormatValues from '../definitions/FooterDefaultIntlFormatValues';
import isVariableInterestRate from '../functions/isVariableInterestRate';
import CardInterestRate from '../components/CardInterestRate';
import CardFeaturesTable from '../components/CardFeaturesTable';
import isARewardsCard from './isARewardsCard';
import isCIBCCreditCard from './isCIBCCreditCard';


function getCardFooterTabContent({ cardInfo, includeRatehubReview }) {
    const {
        name,
        ratehubReview,
        featureDescriptions,
        bonusDescriptionMarkup,
    } = cardInfo;
    const isRewardsCard = isARewardsCard(cardInfo.displayType);
    const willDisplayCardFeaturesTable = !!featureDescriptions.length || isRewardsCard;

    const cardFooterTabContent = [
        // Perks
        (
            <div
                key="perks"
                className="rh-mx-1_5 rh-py-1"
                data-test-name="footer-perks"
            >
                <If condition={willDisplayCardFeaturesTable}>
                    <h3 className="rh-title-3xs">
                        <FormattedMessage
                            {...LOCAL_MESSAGES.featureDescriptionIntro}
                            values={{ name }}
                        />
                    </h3>

                    <div className="card-features-table-wrapper">
                        <CardFeaturesTable
                            featureDescriptions={featureDescriptions}
                            isShowingAllFeatures={isRewardsCard}
                        />
                    </div>
                </If>

                <If condition={bonusDescriptionMarkup}>
                    <h3 className="rh-title-3xs">
                        <FormattedMessage
                            {...LOCAL_MESSAGES.otherPerks}
                            // for Rewards, we always show CardFeaturesTable, so this header will always be 'Other perks'
                            values={{ isOther: willDisplayCardFeaturesTable }}
                        />
                    </h3>

                    <div
                        className="bonus-description-markup rh-text-xs leading-l"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                            __html: bonusDescriptionMarkup,
                        }}
                    />
                </If>
            </div>
        ),

        // Details
        (
            <DetailsContent
                key="details"
                cardInfo={cardInfo}
            />
        ),
    ];

    // Pros & Cons
    if (includeRatehubReview) {
        cardFooterTabContent.splice(1, 0, (
            <div
                key="pros-and-cons"
                className="rh-mx-1_5 rh-py-1"
                data-test-name="footer-pros-and-cons"
            >
                <h3 className="rh-title-3xs">
                    <FormattedMessage
                        id="edb.credit-card.ratehubs-take"
                        defaultMessage="Ratehub’s take"
                    />
                </h3>

                <div
                    className="ratehub-review-markup rh-text-xs leading-l"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: ratehubReview?.content,
                    }}
                />
            </div>
        ));
    }

    return cardFooterTabContent;
}


function DetailsContent({ cardInfo }) {
    const {
        bank,
        annualFee,
        isFirstYearFeeWaived,
        minimumIncome = null,
        minimumCreditScore = null,

        purchaseInterest,
        purchaseInterestMax,
        cashAdvanceInterest,
        cashAdvanceInterestMax,
        balanceTransferInterest,
        balanceTransferInterestMax,

        balanceTransferPromoInterest,
        balanceTransferPromoMonths,
    } = cardInfo;
    const isCIBC = isCIBCCreditCard(cardInfo);
    const hasVariableInterestRate = isVariableInterestRate(bank);

    const BalanceTransferRate = (
        <CardInterestRate
            interestRate={balanceTransferInterest}
            interestRateMax={balanceTransferInterestMax}
            hasVariableInterestRate={hasVariableInterestRate}
            variableClassName="weight-regular"
        />
    );


    const columns = [
        (
            <>
                <h3 className="rh-title-3xs rh-mb-0_5">
                    <FormattedMessage
                        id="edb.credit-card.details.eligibility"
                        defaultMessage="Eligibility requirements"
                    />
                </h3>
                <If condition={minimumCreditScore}>
                    <p
                        className="rh-text-xs rh-mb-0_25"
                        data-test-name="minimum-credit-score-footer"
                    >
                        <FormattedMessage
                            id="edb.credit-card.details.credit-score"
                            defaultMessage="Minimum credit score: <strong>{minimumCreditScore}</strong>"
                            values={{
                                ...DefaultIntlFormatValues,
                                minimumCreditScore,
                            }}
                        />
                    </p>
                </If>
                <p className="rh-text-xs rh-m-0">
                    <FormattedMessage
                        id="edb.credit-card.details.minimum-income"
                        defaultMessage={`Minimum income: <b>{minimumIncome, select,
                                        0 {{notApplicable}}
                                        other {{minimumIncome, number, CAD0}/yr}}</b>`}
                        values={{
                            ...DefaultIntlFormatValues,
                            minimumIncome,
                            notApplicable,
                        }}
                    />
                </p>
            </>
        ),

        (
            <>
                <h3 className="rh-title-3xs rh-mb-0_5">
                    <FormattedMessage
                        id="edb.credit-card.details.interest-rates"
                        defaultMessage="Interest rates"
                        key="interest-rates"
                    />
                </h3>

                {/* always show ALL THREE interest rates, accepting null as N/A (is that you Amex?) */}
                <p className="rh-text-xs rh-mt-0_25 rh-mb-0">
                    <FormattedMessage
                        id="edb.credit-card.details.details.purchase-interest"
                        defaultMessage="Purchase interest: "
                    />
                    <strong data-test-name="purchase-interest-rate">
                        <CardInterestRate
                            interestRate={purchaseInterest}
                            interestRateMax={purchaseInterestMax}
                            hasVariableInterestRate={hasVariableInterestRate}
                            variableClassName="weight-regular"
                        />
                    </strong>
                </p>

                <p className="rh-text-xs rh-mt-0_25 rh-mb-0">
                    <FormattedMessage
                        id="edb.credit-card.details.details.cash-advance"
                        defaultMessage="Cash advance: "
                    />
                    <strong data-test-name="cash-advance-interest-rate">
                        <CardInterestRate
                            interestRate={cashAdvanceInterest}
                            interestRateMax={cashAdvanceInterestMax}
                            hasVariableInterestRate={hasVariableInterestRate}
                            variableClassName="weight-regular"
                        />
                    </strong>
                </p>

                <p
                    className="rh-text-xs rh-mt-0_25 rh-mb-0"
                    data-test-name="balance-transfer-interest-message"
                >
                    <FormattedMessage
                        id="edb.credit-card.details.details.cibc.balance-transfer"
                        // We are assuming that there will never be a _range_ of balanceTransfer interest rates.
                        defaultMessage={
                            `
                            Balance transfer: {balanceTransferPromoInterest, select,
                               undefined {<strong>{BalanceTransferRate}</strong>}
                               other {
                                   <strong>{balanceTransferPromoInterest, number, percent2}</strong>
                                   {balanceTransferPromoMonths, select,
                                       null {}
                                       undefined {}
                                       other {
                                           for the first {balanceTransferPromoMonths, 
                                                plural, one {month} other {# months}}.
                                           <strong>{BalanceTransferRate}</strong>
                                           after promo.
                                       }
                                   }
                               }
                           }
                            `
                        }
                        values={{
                            balanceTransferPromoInterest: balanceTransferPromoInterest == null  // check for null or undefined
                                ? undefined
                                : balanceTransferPromoInterest / 100,
                            balanceTransferPromoMonths,
                            BalanceTransferRate,
                            ...DefaultIntlFormatValues,
                        }}
                    />
                </p>
            </>
        ),
    ];

    if (isCIBC) {
        columns.push((
            <>
                <h3 className="rh-title-3xs rh-mb-0_5">
                    <FormattedMessage
                        id="edb.credit-card.details.cibc-annual-fee"
                        defaultMessage="Annual fee"
                        key="annual-fee"
                    />
                </h3>
                <p className="rh-text-xs rh-mt-0_25 rh-mb-0 weight-medium">
                    <FormattedMessage
                        {...MESSAGES.annualFeeSummary}
                        values={{
                            annualFee,
                            isFirstYearFeeWaived,
                            zero: 0,
                            ...FooterDefaultIntlFormatValues,
                            ...DefaultIntlFormatValues,
                        }}
                    />
                </p>
            </>
        ));
    }

    return (
        <EqualWidthMessageColumns
            className="rh-py-1 rh-mx-1_5"
            data-test-name="footer-details"
            columns={columns}
        />
    );
}

DetailsContent.propTypes = {
    cardInfo: PropTypes.shape(CreditCardShape).isRequired,
};


const LOCAL_MESSAGES = defineMessages({
    otherPerks: {
        id: 'edb.credit-card.footer.perks.subtitle',
        defaultMessage: '{isOther, select, true {Other perks} other {Perks}}',
    },

    featureDescriptionIntro: {
        id: 'edb.credit-card.footer.feature-description-intro',
        defaultMessage: 'Perks of the {name}',
    },
});

// this seems like an odd way to treat an undefined interest rate; what’s up Amex?
// at least it doesn’t need to be translated
const notApplicable = 'N/A';


export default getCardFooterTabContent;
